import { FormControl, Stack, Typography } from '@mui/material';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { useTranslation } from 'react-i18next';
import {
  CardContent,
  ExpandedContent,
  PlanCard,
} from 'features/membership/components/PlanCard';
import { useAuthState } from 'state/useAuthState';
import { useEffect, useRef } from 'react';
import { useCenterPlanCard } from 'features/membership/hooks/useCenterPlanCard';
import { SubscriptionInformation } from 'features/membership/components/SubscriptionInformation';
import { MEMBERSHIP_PLANS } from 'features/membership/membership.constants';
import { usePostHog } from 'posthog-js/react';
import { useSupportUrlState } from 'features/membership/hooks/useSupportUrlState';

const ignorePlans = [MEMBERSHIP_PLANS.proYearly, MEMBERSHIP_PLANS.proMonthly];

export const MembershipContent = (): JSX.Element => {
  const { t } = useTranslation();
  const { plan: selectedPlan, source, setPlan } = useSupportUrlState();
  const { isProMember } = useAuthState();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const posthog = usePostHog();

  useCenterPlanCard({
    containerRef,
    selectedPlan,
    ignorePlans,
  });

  // This fallback shouldn't ideally be necessary, but existing components
  // rely directly on the query param to determine the selected plan.
  // A broader refactor is needed to support a proper default plan
  // when none is selected.
  useEffect(() => {
    if (!selectedPlan && !isProMember) {
      setPlan(MEMBERSHIP_PLANS.proYearly);
    }
  }, [isProMember, selectedPlan, setPlan]);

  const isProSelected =
    selectedPlan === MEMBERSHIP_PLANS.proYearly ||
    selectedPlan === MEMBERSHIP_PLANS.proMonthly;
  const isDonateSelected = selectedPlan === 'donate';
  const isTeamsSelected = selectedPlan === 'teams';

  return (
    <SimplePageLayout
      maxWidth="sm"
      ref={containerRef}
      containerSx={{
        /*
         * Reserves space for the scrollbar on both sides to prevent layout shifts
         * in centered designs when content expands.
         * Supported by all modern browsers.
         */
        scrollbarGutter: 'stable both-edges',
      }}
    >
      <Stack spacing={4}>
        <SubscriptionInformation />

        <FormControl fullWidth>
          <Stack
            spacing={4}
            role="radiogroup"
            aria-label={t('supportUs.supportPlans')}
          >
            {!isProMember && (
              <PlanCard
                name={t('membership.proMembership.title')}
                value={MEMBERSHIP_PLANS.proYearly}
                onChange={(value) => {
                  posthog.capture('toggled_membership_plan', {
                    plan: value,
                    source,
                  });
                  setPlan(value);
                }}
                checked={isProSelected}
                buttonTitle={t('supportUs.proMembership.ctaButton')}
                displayActive // Pro plan is always on for regular members
              >
                <PlanCard.Content>
                  <CardContent
                    title={t('membership.proMembership.title')}
                    plan={MEMBERSHIP_PLANS.proYearly}
                    description={t('membership.proMembership.description')}
                  />
                </PlanCard.Content>
                <PlanCard.CollapseContent>
                  <ExpandedContent plan={MEMBERSHIP_PLANS.proYearly} />

                  <Typography variant="subtitle1">
                    {t('membership.proMembership.footer')}
                  </Typography>
                </PlanCard.CollapseContent>
              </PlanCard>
            )}

            <Stack spacing={2}>
              <Typography variant="h3" sx={{ fontSize: 20 }}>
                {t('membership.otherWaysToSupport')}
              </Typography>

              <PlanCard
                name={t('membership.donate.title')}
                value="donate"
                onChange={(value) => {
                  posthog.capture('toggled_membership_plan', {
                    plan: value,
                    source,
                  });
                  setPlan(value);
                }}
                checked={isDonateSelected}
                buttonTitle={t('membership.donate.ctaButton')}
                bgcolor={
                  isDonateSelected
                    ? 'background.paper'
                    : 'background.membership'
                }
              >
                <PlanCard.Content>
                  <CardContent
                    title={t('membership.donate.title')}
                    description={t('membership.donate.description')}
                  />
                </PlanCard.Content>
                <PlanCard.CollapseContent>
                  <ExpandedContent />
                </PlanCard.CollapseContent>
              </PlanCard>

              <PlanCard
                name={t('supportUs.teams.title')}
                value="teams"
                onChange={(value) => {
                  posthog.capture('toggled_membership_plan', {
                    plan: value,
                    source,
                  });
                  setPlan(value);
                }}
                checked={isTeamsSelected}
                buttonTitle={t('supportUs.teams.ctaButton')}
                bgcolor={
                  isTeamsSelected ? 'background.paper' : 'background.membership'
                }
              >
                <PlanCard.Content>
                  <CardContent
                    title={t('supportUs.teams.title')}
                    description={t('supportUs.teams.description')}
                  />
                </PlanCard.Content>
                <PlanCard.CollapseContent>
                  <ExpandedContent plan="teams" />
                </PlanCard.CollapseContent>
              </PlanCard>
            </Stack>
          </Stack>
        </FormControl>
      </Stack>
    </SimplePageLayout>
  );
};
